"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { useAppKit, useAppKitAccount } from "@reown/appkit/react";
import { useDisconnect } from "wagmi";
import {
  setUser as signupUser, 
  getIPAddress,
  getUserFromSupabase,
  getUserPoints,
} from "@/utils/helpers/helperFunctions";
import { useSearchParams } from "next/navigation";
import { wagmiAdapter, projectId } from "../configs/wagmiConfig";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createAppKit } from "@reown/appkit/react";
import { mainnet, avalanche } from "@reown/appkit/networks";
import { cookieToInitialState, WagmiProvider, type Config } from "wagmi";
import { Provider } from "react-redux";
import store from "../store";
import logWithTimestamp from "@/utils/refactored/helpers";

// Set up queryClient
const queryClient = new QueryClient();

if (!projectId) {
  throw new Error("Project ID is not defined");
}

// Set up metadata
const metadata = {
  name: "MXS Games",
  description: "MXS Games",
  url: "https://metaxseed.io",
  icons: ["https://avatars.githubusercontent.com/u/179229932"],
};

// Create the modal
const modal = createAppKit({
  adapters: [wagmiAdapter],
  projectId,
  networks: [avalanche, mainnet],
  defaultNetwork: avalanche,
  metadata: metadata,
  enableWalletConnect: false,
  allowUnsupportedChain: false,
  themeVariables: {
    "--w3m-z-index": 9999999,
  },
  allWallets: "SHOW",
  features: {
    socials: [],
    email: false,
    onramp: false,
    swaps: false,
    analytics: false,
    history: false,
  },
});

interface UserPoints {
  all_time_mxs_gold_with_bonuses: number;
  [key: string]: any; // for other potential properties
}

interface AuthContextProps {
  loginWithWallet: () => void;
  logout: () => void;
  setCustomWallet: (wallet: string) => void;
  setIsAdminAuth: (isAdmin: boolean) => void;
  user: any;
  userPoints: any;
  chain: any;
  walletAddress: any;
  isAuthenticated: boolean;
  isFetchingUser: boolean;
  isWalletConnected: boolean;
  isSignedUp: boolean;
  isSigningUp: boolean;
  ipAddress: any;
  isAdminAuthenticated: boolean;
  openLoginModal: () => void;
  closeLoginModal: () => void;
  updateUserPoints: (amount: number) => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{
  children: ReactNode;
  cookies: string | null;
}> = ({ children, cookies }) => {
  const initialState = cookieToInitialState(
    wagmiAdapter.wagmiConfig as Config,
    cookies
  );

  return (
    <WagmiProvider
      config={wagmiAdapter.wagmiConfig as Config}
      initialState={initialState}
    >
      <QueryClientProvider client={queryClient}>
        <AuthContextProvider cookies={cookies}>
          <Provider store={store}>{children}</Provider>
        </AuthContextProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
};

const AuthContextProvider: React.FC<{
  children: ReactNode;
  cookies: string | null;
}> = ({ children, cookies }) => {
  const {
    address: walletAddressFromWagmi,
    isConnected: isWalletConnected,
    caipAddress: chain,
    status,
  } = useAppKitAccount();

  const { open: openLoginModal, close: closeLoginModal } = useAppKit();
  const { disconnect } = useDisconnect();
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isFetchingUser, setIsFetchingUser] = useState(false);
  const [userPoints, setUserPoints] = useState<any>(null);
  const [ipAddress, setIpAddress] = useState<string | null>(null);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [isSignedUp, setUserSignedUp] = useState(false);
  const [customWallet, setCustomWallet] = useState<string | null>(null);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const loginInFlightRef = useRef(false);

  const referrerOnSearchParams = useSearchParams()?.get("referrer") || null;

  useEffect(() => {
    setWalletAddress(customWallet || walletAddressFromWagmi || null);
  }, [walletAddressFromWagmi, customWallet]);

  useEffect(() => {
    if (
      isWalletConnected &&
      !walletAddress &&
      (!walletAddressFromWagmi || walletAddressFromWagmi === "undefined")
    ) {
      console.error(
        "Wallet connected but address is undefined. Logging out..."
      );
      logout();
      return;
    }

    // Only attempt login if we have a wallet address, we're not already authenticated, and not currently fetching
    if ((isWalletConnected || walletAddress || walletAddressFromWagmi) && !isAuthenticated && !isFetchingUser && !isSigningUp) {
      loginWithWallet();
    } else if (!isWalletConnected && !walletAddress && !walletAddressFromWagmi) {
      setIsFetchingUser(false);
    }
  }, [isWalletConnected, walletAddress, walletAddressFromWagmi]);

  const loginWithWallet = async () => {
    const walletToUse = customWallet || walletAddress || walletAddressFromWagmi;

    if (!walletToUse || loginInFlightRef.current) {
      return;
    }

    loginInFlightRef.current = true;
    setIsFetchingUser(true);

    try {
      const userResponse = await getUserFromSupabase(walletToUse);

      if (userResponse?.success) {
        setUser(userResponse.data);
        setIsAuthenticated(true);
        return;
      }

      setIsSigningUp(true);
      const signupResponse = await signupUser({
        wallet: walletToUse,
        referred_by_uid: referrerOnSearchParams,
        wallet_autogenerated: false,
        ipAddress: ipAddress,
      });

      if (signupResponse?.success) {
        setUser(signupResponse.data.user);
        setIsAuthenticated(true);
        setUserSignedUp(true);
        return;
      }

      // Another concurrent request may have created the user first
      const retryResponse = await getUserFromSupabase(walletToUse);
      if (retryResponse?.success) {
        setUser(retryResponse.data);
        setIsAuthenticated(true);
        return;
      }

      setIsAuthenticated(false);
      await logout();
    } catch (error) {
      console.error("Login error:", error);
      await logout();
    } finally {
      loginInFlightRef.current = false;
      setIsFetchingUser(false);
      setIsSigningUp(false);
    }
  };

  const getPointsOfUser = async (uid: string) => {
    const points = await getUserPoints({
      uid,
    });

    setUserPoints(points.data);
  };

  useEffect(() => {
    if (user && user.uid && !userPoints) {
      getPointsOfUser(user.uid);
    } else {
      setUserPoints(null);
    }
  }, [user]);

  const logout = async () => {
    logWithTimestamp("logout called");
    setIsAuthenticated(false);
    setUser(null);
    setUserPoints(null);
    setWalletAddress(null);
    setUserSignedUp(false);
    setCustomWallet(null);
    sessionStorage.removeItem("userSession");
    sessionStorage.removeItem("admin");
    localStorage.removeItem("userSession");
    localStorage.removeItem("admin");
    await disconnect();
  };

  useEffect(() => {
    if (!ipAddress) {
      getIPAddress()
        .then((ip) => setIpAddress(ip))
        .catch((error) => {
          console.error("Error getting IP address", error);
          setIpAddress(null);
        });
    }
  }, [ipAddress]);

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("admin");
    if (adminAuth) {
      setIsAdminAuthenticated(true);
    }
  }, []);

  const updateUserPoints = (amount: number) => {
    setUserPoints((prevPoints: UserPoints | null) => ({
      ...prevPoints,
      all_time_mxs_gold_with_bonuses: (prevPoints?.all_time_mxs_gold_with_bonuses || 0) + amount
    }));
  };

  return (
    <AuthContext.Provider
      value={{
        loginWithWallet,
        logout,
        setCustomWallet,
        user,
        chain,
        ipAddress,
        walletAddress,
        isAuthenticated,
        isFetchingUser,
        isWalletConnected,
        userPoints,
        isSignedUp,
        isSigningUp,
        setIsAdminAuth: setIsAdminAuthenticated,
        isAdminAuthenticated,
        openLoginModal,
        closeLoginModal,
        updateUserPoints,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
